import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../App';

const resize = (file) => new Promise((res) => {
  if (!file) return res(null);
  const img = new Image(); const url = URL.createObjectURL(file);
  img.onload = () => { const s = 320 / Math.max(img.width, img.height); const c = document.createElement('canvas'); c.width = Math.round(img.width * s); c.height = Math.round(img.height * s); c.getContext('2d').drawImage(img, 0, 0, c.width, c.height); URL.revokeObjectURL(url); res(c.toDataURL('image/jpeg', 0.85).split(',')[1]); };
  img.src = url;
});

const Field = ({ k, label, placeholder, f, setF }) => (
  <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">{label}</label>
    <input value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} className="input-field h-10 text-sm" placeholder={placeholder} data-testid={`profile-${k}`} /></div>
);

export default function RepProfileCard({ user }) {
  const [f, setF] = useState({ name: '', company: '', phone: '', rep_title: '', rep_photo_b64: '', bio: '' });
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/profile').then(r => {
      const p = r.data.profile || {};
      setF({ name: p.name || '', company: p.company || '', phone: p.phone || '', rep_title: p.rep_title || '', rep_photo_b64: p.rep_photo_b64 || '', bio: p.bio || '' });
      setStats(p.stats);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/profile', f);
      const u = JSON.parse(localStorage.getItem('sls_user') || '{}');
      localStorage.setItem('sls_user', JSON.stringify({ ...u, name: f.name || u.name, company: f.company || u.company }));
      toast.success('Profile saved — new proposals use these details automatically');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };


  return (
    <div className="grid lg:grid-cols-3 gap-5 max-w-5xl" data-testid="rep-profile-card">
      <div className="card p-6 lg:col-span-2">
        <h3 className="font-heading text-base font-semibold text-text-primary mb-1">Rep profile & proposal defaults</h3>
        <p className="text-xs text-text-muted mb-5">Your title, phone and headshot print on every proposal cover and contact card you generate.</p>
        <div className="flex items-center gap-4 mb-5 p-4 rounded-xl bg-surface-alt">
          {f.rep_photo_b64 ? <img src={`data:image/jpeg;base64,${f.rep_photo_b64}`} alt="Headshot" className="w-16 h-16 rounded-2xl object-cover" data-testid="profile-photo-preview" /> : <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-primary text-2xl font-bold">{(f.name || user?.name || 'U').charAt(0)}</div>}
          <div className="flex-1">
            <p className="text-lg font-semibold text-text-primary">{f.name || user?.name}</p>
            <p className="text-sm text-text-muted">{user?.email}</p>
            <span className="inline-flex mt-1 px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[10px] font-bold uppercase">{user?.role}</span>
          </div>
          <label className="text-xs font-bold text-primary cursor-pointer hover:underline" data-testid="profile-photo-label">
            {f.rep_photo_b64 ? 'Change headshot' : 'Upload headshot'}
            <input type="file" accept="image/*" className="hidden" data-testid="profile-photo-input" onChange={e => resize(e.target.files?.[0]).then(b => b && setF(x => ({ ...x, rep_photo_b64: b })))} />
          </label>
          {f.rep_photo_b64 && <button onClick={() => setF({ ...f, rep_photo_b64: '' })} className="text-xs text-text-muted hover:text-destructive" data-testid="profile-photo-remove">Remove</button>}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field k="name" label="Full name" f={f} setF={setF} />
          <Field k="rep_title" label="Title on proposals" placeholder="Solar Energy Advisor" f={f} setF={setF} />
          <Field k="phone" label="Phone" placeholder="(201) 555-0100" f={f} setF={setF} />
          <Field k="company" label="Company" placeholder="Samaritan Solar" f={f} setF={setF} />
        </div>
        <div className="mt-4"><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Short bio</label><textarea value={f.bio} onChange={e => setF({ ...f, bio: e.target.value })} rows={2} className="input-field h-auto py-2 text-sm resize-none" placeholder="12 years helping NJ homeowners go solar…" data-testid="profile-bio" /></div>
        <div className="flex justify-end mt-5"><button onClick={save} disabled={saving} className="text-sm font-bold px-6 py-2.5 rounded-xl text-[#0C2417] disabled:opacity-50" style={{ background: '#FAAB1C' }} data-testid="profile-save-btn">{saving ? <><i className="fa-solid fa-spinner fa-spin mr-1.5" />Saving…</> : <><i className="fa-solid fa-floppy-disk mr-1.5" />Save profile</>}</button></div>
      </div>
      <div className="space-y-4">
        <div className="card p-5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-3">Your numbers</p>
          {[['Leads managed', stats?.leads_managed], ['Deals won', stats?.deals_won], ['Proposals', stats?.proposals_sent], ['Activities', stats?.total_activities]].map(([l, v]) => (
            <div key={l} className="flex justify-between py-2 border-b border-border/60 last:border-0 text-sm"><span className="text-text-secondary">{l}</span><span className="font-mono font-bold text-text-primary">{v ?? '—'}</span></div>
          ))}
        </div>
        <div className="card p-5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Credits</p>
          <p className="font-heading font-black text-3xl text-primary">{user?.credits || 0}</p>
        </div>
      </div>
    </div>
  );
}
